import { z } from "zod";

/** A single taxonomy token value the compiler reads: params.tokens[key].{value,lock}. */
export const TokenValueSchema = z.object({
  // Multi-select tokens come back as string[]. A narrow `z.string()` rejects
  // the whole payload, which blanks the seed list with no error anywhere.
  value: z.union([z.string(), z.array(z.string())]),
  // Loose on purpose: the backend emits locked | soft_locked | flexible (and may
  // add more). A narrow enum rejects the whole seed payload — blanking the grid.
  lock: z.string().default("unlocked"),
});
export type TokenValue = z.infer<typeof TokenValueSchema>;

/** The two keys the backend prompt compiler reads out of params; everything else is ignored. */
export const SeedParamsSchema = z
  .object({
    tokens: z.record(z.string(), TokenValueSchema).optional(),
    contract: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();
export type SeedParams = z.infer<typeof SeedParamsSchema>;

export const CREATIVESPHERE_SEED_TYPES = [
  "product",
  "photography",
  "scene",
  "composition",
  "casting",
] as const;
export type CreativesphereSeedType = (typeof CREATIVESPHERE_SEED_TYPES)[number];

export const SeedMessageSchema = z.object({
  slug: z.string(),
  site_id: z.string(),
  seed_type: z.string(), // intentionally loose: backend may return seed types the FE doesn't enumerate
  name: z.string(),
  description: z.string().default(""),
  params: SeedParamsSchema.default({}),
  tags: z.array(z.string()).default([]),
  // Contract deltas — default gracefully when the backend has not shipped them yet.
  is_protected: z.boolean().default(false),
  is_preset: z.boolean().default(false),
  thumbnail_url: z.string().optional(),
  created_by: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type SeedMessage = z.infer<typeof SeedMessageSchema>;

export const ListSeedsResponseSchema = z.object({
  seeds: z.array(SeedMessageSchema),
  total: z.number().default(0),
});
export type ListSeedsResponse = z.infer<typeof ListSeedsResponseSchema>;

export const AssetMessageSchema = z.object({
  slug: z.string(),
  url: z.string(),
  media_type: z.string().default(""),
  metadata: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string().optional(),
});
export type AssetMessage = z.infer<typeof AssetMessageSchema>;

export const SeedSnapshotSchema = z.object({
  slug: z.string(),
  seed_type: z.string(), // intentionally loose: backend may return seed types the FE doesn't enumerate
  name: z.string(),
  params: SeedParamsSchema.default({}),
  tags: z.array(z.string()).default([]),
});
export type SeedSnapshot = z.infer<typeof SeedSnapshotSchema>;

// Identity fields optional: an expanded preset omits site_id and an inline seed may have no slug.
export const SeedsMapEntrySchema = SeedMessageSchema.partial({
  slug: true,
  site_id: true,
  seed_type: true,
  name: true,
}).extend({
  // grpc-gateway emits unset Struct/Timestamp as null; malformed params degrade to {} rather than rejecting the list.
  params: SeedParamsSchema.extend({
    tokens: z.record(z.string(), TokenValueSchema).catch({}).optional(),
  })
    .nullish()
    .transform((v) => v ?? {})
    .catch({}),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
});
export type SeedsMapEntry = z.infer<typeof SeedsMapEntrySchema>;
// `.catch` collapses z.input to unknown, so params is retyped by hand.
export type SeedsMapEntryInput = Omit<
  z.input<typeof SeedsMapEntrySchema>,
  "params"
> & { params?: z.input<typeof SeedParamsSchema> | null };

export const SeedsMapSchema = z.record(z.string(), SeedsMapEntrySchema);
export type SeedsMap = z.infer<typeof SeedsMapSchema>;
export type SeedsMapInput = Record<string, SeedsMapEntryInput>;

export const GenerationMessageSchema = z.object({
  slug: z.string(),
  site_id: z.string(),
  // Proto models status as a free string ("pending"|"completed"|"failed"); keep it a
  // string so one unexpected/legacy value can't reject an entire ListGenerations page.
  // Pilot flow uses GENERATION_STATUS (below).
  status: z.string(),
  name: z.string().optional(),
  // Blueprint/collection the pilot was created from; optional for legacy generations.
  from_blueprint_id: z.string().optional(),
  collection_id: z.string().optional(),
  seed_snapshots: z.array(SeedSnapshotSchema).default([]),
  // Free-text prompt appended on top of the seeds when this was generated.
  additional_prompt: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  assets: z.array(AssetMessageSchema).default([]),
  created_by: z.string().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type GenerationMessage = z.infer<typeof GenerationMessageSchema>;

/** Pilot lifecycle: created PILOT_GENERATING, then PILOT_READY | PILOT_FAILED. */
export const GENERATION_STATUS = {
  PILOT_GENERATING: "PILOT_GENERATING",
  PILOT_READY: "PILOT_READY",
  PILOT_FAILED: "PILOT_FAILED",
} as const;
export type GenerationStatus =
  (typeof GENERATION_STATUS)[keyof typeof GENERATION_STATUS];

// Statuses where polling stops; includes the legacy proto strings ("completed"/"failed").
const TERMINAL_GENERATION_STATUSES: ReadonlySet<string> = new Set([
  GENERATION_STATUS.PILOT_READY,
  GENERATION_STATUS.PILOT_FAILED,
  "completed",
  "failed",
]);

/** True once a generation has settled and no longer needs polling. */
export function isTerminalGenerationStatus(status: string): boolean {
  return TERMINAL_GENERATION_STATUSES.has(status);
}

/** Body for POST /v1/{site_id}/generations — kicks off a pilot. */
export interface CreateGenerationRequest {
  from_blueprint_id: string;
  collection_id?: string;
  name?: string;
  propagation_id?: string;
}

export const GenerateResponseSchema = z.object({
  generation: GenerationMessageSchema,
});
export type GenerateResponse = z.infer<typeof GenerateResponseSchema>;

export const ListGenerationsResponseSchema = z.object({
  generations: z.array(GenerationMessageSchema),
  total: z.number().default(0),
});
export type ListGenerationsResponse = z.infer<
  typeof ListGenerationsResponseSchema
>;

/**
 * A blueprint: a named, premade set of seeds (as a {@link SeedsMap}) plus a
 * thumbnail, chosen in the Step 2 picker. Selecting one seeds a pilot
 * generation. `slug` is the identifier the backend ships — there is no separate
 * `id`, and no description. Everything past `slug`/`name` is optional so a
 * partially-populated blueprint still renders in the grid.
 */
export const BlueprintMessageSchema = z.object({
  slug: z.string(),
  name: z.string(),
  thumbnail_url: z.string().optional(),
  seeds_map: SeedsMapSchema.default({}),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type BlueprintMessage = z.infer<typeof BlueprintMessageSchema>;

/** `GET /v1/{siteId}/blueprints/{id}` wraps the blueprint, like GenerateResponse. */
export const GetBlueprintResponseSchema = z.object({
  blueprint: BlueprintMessageSchema,
});
export type GetBlueprintResponse = z.infer<typeof GetBlueprintResponseSchema>;

export const ListBlueprintsResponseSchema = z.object({
  blueprints: z.array(BlueprintMessageSchema),
  // Optional on the wire (the doc'd response is just `{ blueprints }`); default
  // keeps callers that read `total` working whether or not the backend sends it.
  total: z.number().default(0),
});
export type ListBlueprintsResponse = z.infer<
  typeof ListBlueprintsResponseSchema
>;

/** GenerateRequest.options — the four string keys the backend reads. */
export interface GenerateOptions {
  intent?: string;
  classification?: string;
  aspect_ratio?: string;
  negative_prompt?: string;
}

/**
 * POST /v1/generate body. Matches GenerationService.Generate (GenerateRequest).
 *
 * A request supplies its seeds one of two ways:
 * - `seed_refs`: an ordered list of seed slugs (the original GenLab path), or
 * - `seeds_map`: a full snapshot of seed fields by type (the blueprint/pilot
 *   path — see SeedsMapSchema).
 *
 * `propagation_id` associates the resulting generation with a propagation
 * (e.g. the pilot); adding `propagation_item_id` as well ties it to a single
 * item (used when regenerating one item in a collection).
 *
 * `group_id` / `group_type` identify the target product group. `group_type` is a
 * proto `ProductGroupType` value, mapped from the URL's collection/category/tag.
 */
export interface GenerateRequest {
  site_id: string;
  model?: string;
  base_prompt?: string;
  seed_refs?: string[];
  product_image_url?: string;
  options?: GenerateOptions;
  seeds_map?: SeedsMapInput;
  propagation_id?: string;
  propagation_item_id?: string;
  group_id?: string;
  group_type?:
    | "PRODUCT_GROUP_TYPE_UNSPECIFIED"
    | "PRODUCT_GROUP_TYPE_COLLECTION"
    | "PRODUCT_GROUP_TYPE_TAG"
    | "PRODUCT_GROUP_TYPE_CATEGORY";
}

/** Body for CreateSeed / UpdateSeed — the writable subset of SeedMessage. */
export interface SeedWriteBody {
  seed_type: CreativesphereSeedType;
  name: string;
  description?: string;
  params: SeedParams;
  tags?: string[];
  thumbnail_url?: string;
}
